/** Resilient fetch helper for the Outlook bridge proxy.
 *
 *  Same shape as the native fetch API but adds:
 *
 *    - JSON-or-friendly-error contract: never returns a Cloudflare
 *      DOCTYPE page to the caller. If the response Content-Type isn't
 *      JSON OR JSON.parse fails, we wrap with a structured error
 *      object and surface a human-friendly message.
 *
 *    - Frontend-side retry chain on top of the daemon proxy's own
 *      retries. The daemon already retries 3x on 502/503/504 +
 *      ECONN* with 200ms / 1s / 5s backoff. We add ONE more layer
 *      on the browser side (1s / 3s / 10s) because Cloudflare can
 *      time out the original request before the daemon finishes its
 *      retry chain. By the time both layers exhaust, we've spent ~30s
 *      retrying and surface a clean error.
 *
 *    - AbortController support: callers pass an external signal to
 *      cancel mid-flight (e.g. unmounted components).
 *
 *    - Module-level GET response cache. Survives React component
 *      remount, so navigating Mail → Today → Mail returns instantly
 *      from cache instead of refetching a 919-row inbox. TTL by URL
 *      pattern (mirrors the daemon proxy's own cache). Mutating
 *      requests automatically invalidate matching cache prefixes.
 *      Callers can opt out per-call via `noCache: true`.
 *
 *  Caveats:
 *    - Only safe for idempotent reads (GET) and bridge surfaces that
 *      are themselves idempotent (mark, batch-mark, processed). The
 *      caller decides retry policy via `retries` (default 3 for GET,
 *      1 for everything else).
 *    - `noCache: true` skips the cache entirely. Use for force-refresh
 *      or when the caller has no idea whether the response is stale. */

import { noteCacheHeaders } from "./cacheState.js";
import { getCsrfToken, refreshAuth } from "./authState.js";
import { anySignal } from "./signals.js";

interface CacheEntry {
  body: unknown;
  status: number;
  /** Time after which `body` is fresh-good. */
  expiresAt: number;
  /** Time after which we won't serve `body` at all (forced refetch).
   *  Between expiresAt and staleUntil we serve cached AND kick a
   *  background refresh (stale-while-revalidate). */
  staleUntil: number;
}

const CACHE = new Map<string, CacheEntry>();
const CACHE_MAX = 256;

/** One shared network request for a cacheable GET, and everyone waiting on it.
 *
 *  Coalescing used to hand every caller the SAME promise, governed by the FIRST
 *  caller's AbortSignal. When that caller was superseded (the list effect firing
 *  twice after Sync, a remount, a rapid folder click) its abort resolved the
 *  shared promise as "aborted", every other caller treated that as "someone
 *  newer owns the loading state", and nobody did: the list sat on its skeleton
 *  forever.
 *
 *  Now the request runs under its own controller. Each caller holds a waiter
 *  slot; a caller whose signal aborts gives up only its own slot (and gets its
 *  own silent abort), and the shared request is cancelled only when the last
 *  waiter has gone. A caller is therefore never answered "aborted" because of
 *  somebody else's signal. */
type Inflight = {
  promise: Promise<BridgeFetchResult<unknown>>;
  controller: AbortController;
  waiters: number;
  priority: "user" | "background";
};
const INFLIGHT = new Map<string, Inflight>();

/** Bumped by every invalidation. A request that started before the latest
 *  invalidation must not write its (pre-mutation) answer into the cache. */
let cacheEpoch = 0;

/** Endpoints we're currently revalidating in the background. Keeps
 *  the SWR refetch from being launched twice if two components ask
 *  for the same expired entry within the same render tick. */
const REVALIDATING = new Set<string>();

/** TTL by URL pattern. Two windows per endpoint:
 *  - `fresh`: data served without any network call.
 *  - `stale`: data served immediately AND a background revalidation
 *    fires. After this window, the next caller pays a synchronous
 *    refetch.
 *
 *  Tuning principles (post-2026-05-11 dispatch):
 *  - The bridge is the source of truth but doesn't need to be hit on
 *    every navigation. Most data is stable on second-time-back-to-
 *    folder, and even when stale by a few seconds the user-perceived
 *    experience is better than a spinner.
 *  - Mutations invalidate their own prefixes so user actions still
 *    feel responsive (mark/move/categorize -> the affected list
 *    reloads).
 *  - Stale windows are generous because the only cost of stale-then-
 *    refresh is a few seconds of slightly-old data; a navigation
 *    spinner is the worse outcome.
 *
 *  These defaults are overridable at runtime via setBridgeCacheTtls()
 *  (Item C: the /settings/mail/cache page wires them to a
 *  user_settings k/v entry, so the operator can tune without a deploy). */
export type BridgeCacheTtlKey =
  | "folders_tree"
  | "folders_counts"
  | "folders_paths"
  | "categories"
  | "custom_folders"
  | "messages_list"
  | "messages_detail"
  | "classifier_status"
  | "priority_counts"
  | "calendar_events"
  | "healthz";

export interface BridgeCacheTtl {
  freshMs: number;
  staleMs: number;
}

export const BRIDGE_CACHE_TTL_DEFAULTS: Record<BridgeCacheTtlKey, BridgeCacheTtl> = {
  folders_tree:      { freshMs: 5 * 60_000,  staleMs: 30 * 60_000 },
  folders_counts:    { freshMs: 60_000,      staleMs: 5 * 60_000 },
  folders_paths:     { freshMs: 5 * 60_000,  staleMs: 30 * 60_000 },
  categories:        { freshMs: 5 * 60_000,  staleMs: 30 * 60_000 },
  custom_folders:    { freshMs: 10 * 60_000, staleMs: 60 * 60_000 },
  messages_list:     { freshMs: 30_000,      staleMs: 5 * 60_000 },
  messages_detail:   { freshMs: 30_000,      staleMs: 5 * 60_000 },
  classifier_status: { freshMs: 5_000,       staleMs: 30_000 },
  priority_counts:   { freshMs: 60_000,      staleMs: 10 * 60_000 },
  // Short: a stale calendar answer shows a meeting where it no longer is
  // (2026-10-02). Pages also drop it on the daemon's calendar.updated.
  calendar_events:   { freshMs: 30_000,      staleMs: 2 * 60_000 },
  healthz:           { freshMs: 30_000,      staleMs: 5 * 60_000 },
};

/** Per-key TTL labels for the settings UI. */
export const BRIDGE_CACHE_TTL_LABELS: Record<BridgeCacheTtlKey, { label: string; hint: string }> = {
  folders_tree:      { label: "Folder tree",          hint: "Outlook folder hierarchy. Rarely changes." },
  folders_counts:    { label: "Folder counts",        hint: "Per-folder total/unread badges. Updates as mail arrives." },
  folders_paths:     { label: "Folder paths",         hint: "Flat list of folder paths for editors. Rarely changes." },
  categories:        { label: "Outlook categories",   hint: "Master category list. Rarely changes." },
  custom_folders:    { label: "Custom folder defs",   hint: "User-defined virtual folders. Edited from settings." },
  messages_list:     { label: "Message list",         hint: "Per-folder message headers. New mail arrives via SSE." },
  messages_detail:   { label: "Mail body + detail",   hint: "Per-message body + Wllm* fields. Item B keeps this short so classifications surface promptly." },
  classifier_status: { label: "Classifier status",    hint: "Lightweight pill on the AI panel." },
  priority_counts:   { label: "AI priority counts",   hint: "P1-P5 counts; refreshes as classifications complete." },
  calendar_events:   { label: "Calendar events",      hint: "Recipient calendar conflict preview." },
  healthz:           { label: "Bridge /healthz",      hint: "Tiny health pings; the synthetic probe is daemon-side." },
};

const URL_PATTERN_BY_KEY: Record<BridgeCacheTtlKey, RegExp> = {
  folders_tree:      /\/api\/bridge\/v1\/folders\/tree/,
  folders_counts:    /\/api\/bridge\/v1\/folders\/counts/,
  folders_paths:     /\/api\/bridge\/v1\/folders\/paths/,
  categories:        /\/api\/bridge\/v1\/categories/,
  custom_folders:    /\/api\/bridge\/v1\/custom-folders/,
  messages_list:     /\/api\/bridge\/v1\/messages\?/,
  messages_detail:   /\/api\/bridge\/v1\/messages\/[^/]+$/,
  classifier_status: /\/api\/bridge\/v1\/llm\/classifier-status/,
  priority_counts:   /\/api\/bridge\/v1\/llm\/priority-counts/,
  calendar_events:   /\/api\/bridge\/v1\/calendar\/events/,
  healthz:           /\/api\/bridge\/healthz/,
};

const TTL_BY_KEY: Record<BridgeCacheTtlKey, BridgeCacheTtl> = { ...BRIDGE_CACHE_TTL_DEFAULTS };

/** Replace one or more TTL entries at runtime. Called from the
 *  /settings/mail/cache page on save and on app boot once we've
 *  loaded the user_settings overlay. Unknown keys are ignored;
 *  freshMs / staleMs are clamped to >= 0 and freshMs <= staleMs. */
export function setBridgeCacheTtls(overlay: Partial<Record<BridgeCacheTtlKey, Partial<BridgeCacheTtl>>>): void {
  for (const k of Object.keys(overlay) as BridgeCacheTtlKey[]) {
    if (!(k in TTL_BY_KEY)) continue;
    const incoming = overlay[k] ?? {};
    const fresh = Math.max(0, incoming.freshMs ?? TTL_BY_KEY[k].freshMs);
    const stale = Math.max(fresh, incoming.staleMs ?? TTL_BY_KEY[k].staleMs);
    TTL_BY_KEY[k] = { freshMs: fresh, staleMs: stale };
  }
}

export function getBridgeCacheTtls(): Record<BridgeCacheTtlKey, BridgeCacheTtl> {
  return { ...TTL_BY_KEY };
}

function ttlFor(url: string): BridgeCacheTtl {
  for (const k of Object.keys(URL_PATTERN_BY_KEY) as BridgeCacheTtlKey[]) {
    if (URL_PATTERN_BY_KEY[k].test(url)) return TTL_BY_KEY[k];
  }
  return { freshMs: 0, staleMs: 0 };
}

/** Drop every cache entry whose URL contains the prefix. Mutating
 *  callers (mark / move / categorize / batch-mark / send / etc.) call
 *  this with the relevant prefix so the next list refetch sees the
 *  new state.
 *
 *  Also detaches matching in-flight requests (their current waiters still get
 *  their answer, but nobody new joins a request that started before the
 *  mutation) and bumps the epoch so those requests do not write the cache. */
export function invalidateBridgeCache(prefix: string): void {
  cacheEpoch += 1;
  for (const k of Array.from(CACHE.keys())) {
    if (k.includes(prefix)) CACHE.delete(k);
  }
  for (const k of Array.from(INFLIGHT.keys())) {
    if (k.includes(prefix)) INFLIGHT.delete(k);
  }
}

/** Every cache a change to one message can make stale: lists, bodies, search
 *  results, and all the count endpoints the sidebar badges read. Called after
 *  every successful mail mutation, whichever helper sent it. */
export const MAIL_MUTATION_PREFIXES = [
  "/api/bridge/v1/messages",
  "/api/bridge/v1/search",
  "/api/mail/search",
  "/api/bridge/v1/folders/counts",
  "/api/bridge/v1/custom-folders/counts",
  "/api/bridge/v1/llm/priority-counts",
  "/api/bridge/v1/tasks",
] as const;

export function invalidateMailCaches(): void {
  for (const p of MAIL_MUTATION_PREFIXES) invalidateBridgeCache(p);
}

/** Drop the entire cache. Power-user escape hatch (manual refresh
 *  button, sign-out, etc). */
export function clearBridgeCache(): void {
  cacheEpoch += 1;
  CACHE.clear();
  INFLIGHT.clear();
}

// ── Concurrency semaphore + abort registry (added 2026-05-11 after
// the unified RefreshTick regression). Two problems we hit when
// Daily Summary fully expanded:
//   1. A few hundred VirtualFolderCountBadge subscribers all fire
//      pageSize=1 fetches on the same 30 s tick. The browser's
//      HTTP/2 pool saturates and the bridge serializes; the user's
//      next nav request queues behind hundreds of in-flight badge
//      fetches.
//   2. On logout the SPA tries to navigate to /auth/logout but the
//      browser waits for in-flight fetches to settle before unloading
//      the page. With 100+ in-flight, that's >20 s.
//
// Fixes:
//   - MAX_CONCURRENT caps simultaneous in-flight inner fetches to 4.
//     Excess await on a queue. Coalesced cache-hit responses don't
//     count against the budget. SWR background revalidations DO
//     count (they share the same fetch path).
//   - INFLIGHT_CONTROLLERS tracks every AbortController in flight so
//     abortAllBridgeFetches() can tear them down on logout without
//     waiting for natural resolution.
//
// Abort reason codes are passed to AbortController.abort(reason) so
// the catch block can distinguish a real timeout from a navigation /
// bulk cancel. Without this, every navigation-driven supersession
// would propagate up to the UI as "request cancelled" the way it did
// in the 2026-05-11 dispatch regression.
const ABORT_REASON_TIMEOUT = "wov:bridge:timeout";
const ABORT_REASON_BULK = "wov:bridge:bulk";
// User-initiated fetches preempt all in-flight background work via
// this reason code so the catch block can drop the silenced backgrounds
// cleanly without surfacing a banner.
const ABORT_REASON_USER_PREEMPT = "wov:bridge:user-preempt";
// The last waiter on a shared (coalesced) request went away.
const ABORT_REASON_ABANDONED = "wov:bridge:abandoned";
// Backstop cap on simultaneous in-flight inner fetches. Excess await
// on a queue. With the new priority system below most background work
// is gated by user activity rather than the cap, but the cap still
// stops a single page from opening 100 simultaneous browser sockets
// when the user expands a panel with many badge subscribers.
const MAX_CONCURRENT = 6;
// After a user-initiated fetch lands, background fetches stay paused
// for this long. Lets the next user click jump straight to the bridge
// without contending with badge refresh storms. Empirically 5 s is
// long enough to cover a click-rapidly-through-panels session and
// short enough that idle users get fresh counts soon after they stop.
const BACKGROUND_GRACE_MS = 5_000;
let activeFetches = 0;
const fetchQueue: Array<() => void> = [];
const INFLIGHT_CONTROLLERS = new Set<AbortController>();
// Subset of INFLIGHT_CONTROLLERS that are background-priority. User
// fetches abort everything in this set on entry so the user's request
// doesn't have to wait for badge work to finish.
const BACKGROUND_REGISTRY = new Set<AbortController>();
let userFetchesInFlight = 0;
let backgroundsBlockedUntil = 0;

function abortAllBackgroundFetches(reason: string): void {
  for (const ctrl of Array.from(BACKGROUND_REGISTRY)) {
    try { ctrl.abort(reason); } catch { /* already aborted */ }
  }
  BACKGROUND_REGISTRY.clear();
}

function acquireSlot(): Promise<void> {
  if (activeFetches < MAX_CONCURRENT) {
    activeFetches += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    fetchQueue.push(() => {
      activeFetches += 1;
      resolve();
    });
  });
}

function releaseSlot(): void {
  activeFetches = Math.max(0, activeFetches - 1);
  const next = fetchQueue.shift();
  if (next) next();
}

/** Abort every in-flight bridge fetch and clear the queued waiters.
 *  Called on logout so the SPA doesn't sit waiting for 100+ pending
 *  pageSize=1 count fetches before the browser will unload the page.
 *  Aborts carry ABORT_REASON_BULK so the fetch loop returns a silent
 *  aborted error instead of retrying the failed request three times. */
export function abortAllBridgeFetches(): void {
  for (const ctrl of Array.from(INFLIGHT_CONTROLLERS)) {
    try { ctrl.abort(ABORT_REASON_BULK); } catch { /* already aborted */ }
  }
  INFLIGHT_CONTROLLERS.clear();
  // Also drain the queue: pending acquireSlot() awaiters get woken so
  // their fetch loop runs once, sees the abort signal, and bails.
  while (fetchQueue.length > 0) {
    const fn = fetchQueue.shift();
    try { fn?.(); } catch { /* ignore */ }
  }
  activeFetches = 0;
}

export type BridgeFetchOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Default 3 for GET, 1 otherwise. */
  retries?: number;
  /** Default 30s. */
  timeoutMs?: number;
  /** Skip the GET response cache for this call. Use for force-refresh
   *  flows (manual refresh button, post-mutation reconcile). A no-cache call
   *  never joins an in-flight request either: it always asks the bridge. */
  noCache?: boolean;
  /** "user" — the user is actively waiting on this response (folder
   *  click, message open, Reply send). Preempts background work and
   *  installs a 5 s grace window that blocks new background fetches.
   *  "background" (default) — silent maintenance work (badge counts,
   *  AI priority refresh, polling tick). Yields to user activity. */
  priority?: "user" | "background";
  /** What to tell the user when this request hits its own timeout. Without
   *  it the message is the generic "Outlook took too long" one. */
  timeoutMessage?: string;
};

export type BridgeError = {
  /** True if the daemon proxy returned a structured 502 envelope. */
  bridgeUnreachable: boolean;
  /** HTTP status if available (0 = no response, e.g. abort). */
  status: number;
  /** Friendly message safe to surface in UI. */
  message: string;
  /** Detail for debug logs (raw body etc.). Don't surface in UI. */
  detail?: string;
  /** True if the response body was HTML, the Cloudflare /
   *  proxy / SPA-static fallback case the user keeps hitting. */
  htmlResponse: boolean;
  /** True when the caller's own AbortSignal fired (rapid nav,
   *  unmount, logout). Callers should treat this as a silent no-op,
   *  not a real failure: the new in-flight request will supply data.
   *  Render no banner, no Retry, no "request cancelled" text. */
  aborted?: boolean;
  /** Why a silent abort happened. "caller" is the only kind a user-priority
   *  caller can ever receive (apart from "bulk" at logout). */
  abortKind?: "caller" | "bulk" | "yield" | "abandoned";
  /** The bridge HTTP listener answered its lightweight health probe,
   *  but a COM-backed request timed out. Outlook is busy, or blocked by a
   *  sign-in or modal dialog. */
  comBlocked?: boolean;
  /** The request hit its own client-side timeout. */
  timedOut?: boolean;
};

/** True when the bridge error came from a caller-initiated abort
 *  (rapid navigation, unmount, logout). The UI must NOT render an
 *  error banner or Retry for this. A genuine timeout abort (no
 *  external signal) still bubbles through as bridgeUnreachable so
 *  the Retry path stays intact for real failures. */
export function isAbortError(err: BridgeError): boolean {
  return err.aborted === true;
}

export type BridgeFetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; error: BridgeError };

const DEFAULT_TIMEOUT_MS = 30_000;
const RETRY_DELAYS_MS = [1_000, 3_000, 10_000];

/** The generic timeout copy. It deliberately does not claim a cause it cannot
 *  know: a timeout can be a wedged Outlook, a sign-in dialog in the VM, or just
 *  a genuinely large query queued behind other COM work. */
const TIMEOUT_MESSAGE_BUSY =
  "Outlook took too long to answer. It may be busy with a large query, or waiting on a sign-in or dialog in the Windows VM. Retry in a moment.";
const TIMEOUT_MESSAGE_DOWN =
  "Mail bridge did not respond. Check that Classic Outlook is running in the Windows VM, then retry.";
const TIMEOUT_MESSAGE_SEARCH =
  "The search took too long. Try fewer or more specific words, or a sender's address, and search again.";

function defaultTimeoutMessage(url: string): string | undefined {
  return url.includes("/api/bridge/v1/search") || url.includes("/api/mail/search") ? TIMEOUT_MESSAGE_SEARCH : undefined;
}

function storeInCache(cacheKey: string, epochAtStart: number, result: BridgeFetchResult<unknown>): void {
  if (!result.ok || result.status < 200 || result.status >= 300) return;
  if (epochAtStart !== cacheEpoch) return; // invalidated while in flight
  const ttl = ttlFor(cacheKey);
  if (ttl.freshMs <= 0) return;
  // Cap at CACHE_MAX with naive LRU-ish eviction (drop oldest).
  if (CACHE.size >= CACHE_MAX) {
    const first = CACHE.keys().next();
    if (!first.done) CACHE.delete(first.value);
  }
  const ts = Date.now();
  CACHE.set(cacheKey, {
    body: result.data,
    status: result.status,
    expiresAt: ts + ttl.freshMs,
    staleUntil: ts + ttl.staleMs,
  });
}

/** Join a shared request with this caller's own waiter slot. */
function joinInflight<T>(entry: Inflight, signal: AbortSignal | undefined): Promise<BridgeFetchResult<T>> {
  if (signal?.aborted) return Promise.resolve(abortedSilent("caller signal already aborted", "caller"));
  entry.waiters += 1;
  return new Promise<BridgeFetchResult<T>>((resolve) => {
    let settled = false;
    const onAbort = () => {
      if (settled) return;
      settled = true;
      entry.waiters -= 1;
      if (entry.waiters <= 0) {
        try { entry.controller.abort(ABORT_REASON_ABANDONED); } catch { /* already aborted */ }
      }
      resolve(abortedSilent("caller signal aborted while waiting on a shared request", "caller"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    void entry.promise.then((res) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      entry.waiters -= 1;
      resolve(res as BridgeFetchResult<T>);
    });
  });
}

export async function bridgeFetch<T = unknown>(
  url: string,
  opts: BridgeFetchOptions = {},
): Promise<BridgeFetchResult<T>> {
  const method = opts.method ?? "GET";
  const maxTries = Math.max(1, opts.retries ?? (method === "GET" ? 3 : 1));
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const isUser = opts.priority === "user";

  // Cross-tab navigation cache. GET-only, TTL-by-URL-pattern, opt-out
  // via opts.noCache. Cache hit returns instantly without a network
  // round trip; Mail page remount on tab nav reads from here. Two
  // windows: fresh (no network) and stale (serve cached + revalidate
  // in background).
  const cacheKey = method === "GET" && !opts.noCache ? url : null;
  const ttl = cacheKey ? ttlFor(cacheKey) : { freshMs: 0, staleMs: 0 };
  const coalesce = !!cacheKey && ttl.freshMs > 0;
  if (cacheKey && coalesce) {
    const hit = CACHE.get(cacheKey);
    const now = Date.now();
    if (hit && hit.expiresAt > now) {
      // Fully fresh, serve without touching the network.
      return { ok: true, status: hit.status, data: hit.body as T };
    }
    if (hit && hit.staleUntil > now) {
      // Stale but within the SWR window. Serve immediately AND fire a
      // background revalidation if we're not already revalidating. The
      // caller has its answer instantly; the cache updates silently
      // for the next reader.
      if (!REVALIDATING.has(cacheKey)) {
        REVALIDATING.add(cacheKey);
        const epochAtStart = cacheEpoch;
        // SWR revalidate is always background regardless of the
        // originating caller's priority, and it is not tied to the caller's
        // signal: the caller already has its answer. We deliberately don't
        // surface revalidation failures: the user has the stale payload.
        void runFetchLoop<T>(url, method, { ...opts, signal: undefined, priority: "background" }, maxTries, timeoutMs)
          .then((fresh) => storeInCache(cacheKey, epochAtStart, fresh as BridgeFetchResult<unknown>))
          .finally(() => REVALIDATING.delete(cacheKey));
      }
      return { ok: true, status: hit.status, data: hit.body as T };
    }
    // Coalesce in-flight: two simultaneous remounts asking for the same URL
    // share one network call, each with its own waiter slot. A user who is
    // waiting never joins a background request, because background work can
    // be silently dropped to make room for the user.
    const existing = INFLIGHT.get(cacheKey);
    if (existing && !(isUser && existing.priority === "background")) {
      const joined = await joinInflight<T>(existing, opts.signal);
      return rescueIfAbortedForSomeoneElse(joined, url, opts);
    }
  }

  if (method !== "GET") {
    // Mutations invalidate cache prefixes up front so nothing reads the old
    // state while the write is in flight, and again below on success.
    invalidateForMutation(url);
  }

  if (!coalesce || !cacheKey) {
    const result = await runFetchLoop<T>(url, method, opts, maxTries, timeoutMs);
    if (method !== "GET" && result.ok) invalidateForMutation(url);
    return result;
  }

  // Shared request under its own controller; the originating caller is the
  // first waiter.
  const controller = new AbortController();
  const epochAtStart = cacheEpoch;
  const entry: Inflight = {
    controller,
    waiters: 0,
    priority: isUser ? "user" : "background",
    promise: Promise.resolve() as unknown as Promise<BridgeFetchResult<unknown>>,
  };
  entry.promise = runFetchLoop<T>(url, method, { ...opts, signal: controller.signal }, maxTries, timeoutMs)
    .then((result) => {
      storeInCache(cacheKey, epochAtStart, result as BridgeFetchResult<unknown>);
      return result as BridgeFetchResult<unknown>;
    })
    .finally(() => {
      if (INFLIGHT.get(cacheKey) === entry) INFLIGHT.delete(cacheKey);
    });
  INFLIGHT.set(cacheKey, entry);
  const own = await joinInflight<T>(entry, opts.signal);
  return rescueIfAbortedForSomeoneElse(own, url, opts);
}

/** Belt and braces for the invariant above: a user-priority caller whose own
 *  signal is still live must never be handed a silent abort, because it will
 *  (correctly) treat that as "a newer request owns the UI" and stop. If it
 *  happens anyway, ask the bridge again on this caller's behalf, uncoalesced. */
async function rescueIfAbortedForSomeoneElse<T>(
  res: BridgeFetchResult<T>,
  url: string,
  opts: BridgeFetchOptions,
): Promise<BridgeFetchResult<T>> {
  if (res.ok || !res.error.aborted) return res;
  if (opts.priority !== "user") return res;
  if (opts.signal?.aborted) return res;
  if (res.error.abortKind === "bulk") return res;
  return bridgeFetch<T>(url, { ...opts, noCache: true });
}

/** Mutations invalidate cache prefixes so the next list refetch sees
 *  the new state. Best-effort heuristic by URL shape. */
function invalidateForMutation(url: string): void {
  if (/\/v1\/messages(\/|\b)/.test(url) || /\/v1\/folders\/[^/]+\/mark-all-read/.test(url)) {
    invalidateMailCaches();
  }
  if (/\/v1\/custom-folders/.test(url)) invalidateBridgeCache("/api/bridge/v1/custom-folders");
  if (/\/v1\/folders\b/.test(url)) invalidateBridgeCache("/api/bridge/v1/folders");
  if (/\/v1\/calendar/.test(url)) invalidateBridgeCache("/api/bridge/v1/calendar");
}

/** Build the silent-aborted error envelope. Centralized so every
 *  caller-driven abort returns the same shape (aborted: true, empty
 *  message, bridgeUnreachable: false). Consumers MUST treat this as
 *  a no-op: no banner, no Retry, no skeleton-to-error transition. */
function abortedSilent(detail: string, abortKind: BridgeError["abortKind"]): BridgeFetchResult<never> {
  return { ok: false, error: {
    bridgeUnreachable: false,
    status: 0,
    message: "",
    detail,
    htmlResponse: false,
    aborted: true,
    abortKind,
  }};
}

/** Distinguish "bridge process is down" from "bridge process is up but
 *  Outlook's single COM thread is busy or blocked". The latter was observed
 *  live with an OpenText MFA modal: /healthz stayed green while every
 *  folder/message request wedged. Keep this probe tiny and bounded so
 *  error classification cannot start another COM request or retry
 *  storm. */
async function probeBridgeHttpLiveness(): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 3_000);
  try {
    const response = await fetch("/api/bridge/healthz", {
      credentials: "same-origin",
      cache: "no-store",
      signal: ctrl.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function runFetchLoop<T>(
  url: string,
  method: string,
  opts: BridgeFetchOptions,
  maxTries: number,
  timeoutMs: number,
): Promise<BridgeFetchResult<T>> {
  // Fast-path short-circuit: caller's signal already fired before we
  // even started. Don't burn a semaphore slot to immediately throw.
  if (opts.signal?.aborted) {
    return abortedSilent("caller signal already aborted at fetch entry", "caller");
  }
  const isUser = opts.priority === "user";
  // Background fetches yield to user activity. Silent drop when a user
  // fetch is in flight OR within the 5 s grace window after one
  // landed. The user's next click never queues behind badge work.
  if (!isUser && (userFetchesInFlight > 0 || Date.now() < backgroundsBlockedUntil)) {
    return abortedSilent("background fetch yielded to user activity", "yield");
  }
  if (isUser) {
    userFetchesInFlight++;
    // Kick any in-flight backgrounds out of the way so this user
    // fetch grabs the next free semaphore slot. Their catch blocks
    // return silent via the ABORT_REASON_USER_PREEMPT check below.
    abortAllBackgroundFetches(ABORT_REASON_USER_PREEMPT);
  }
  try {
    return await runFetchLoopInner<T>(url, method, opts, maxTries, timeoutMs, isUser);
  } finally {
    if (isUser) {
      userFetchesInFlight = Math.max(0, userFetchesInFlight - 1);
      if (userFetchesInFlight === 0) {
        backgroundsBlockedUntil = Date.now() + BACKGROUND_GRACE_MS;
      }
    }
  }
}

function isCsrfRejection(status: number, text: string): boolean {
  return status === 403 && /csrf_token_(required|mismatch)/.test(text);
}

async function runFetchLoopInner<T>(
  url: string,
  method: string,
  opts: BridgeFetchOptions,
  maxTries: number,
  timeoutMs: number,
  isUser: boolean,
): Promise<BridgeFetchResult<T>> {
  let lastError: BridgeError = {
    bridgeUnreachable: true,
    status: 0,
    message: "no attempt",
    htmlResponse: false,
  };
  // A CSRF 403 gets exactly one refresh-and-retry, on top of the retry budget.
  let csrfRetried = false;

  for (let attempt = 1; attempt <= maxTries; attempt++) {
    // Concurrency cap: wait for a slot before firing the network
    // request. Excess fetches queue; abortAllBridgeFetches() drains
    // the queue immediately on logout so the browser unload is fast.
    await acquireSlot();
    // Caller may have aborted WHILE we were waiting in the queue
    // (Daily Summary expanded + rapid panel nav). Release the slot
    // for the next waiter and return silent — don't fire a doomed
    // network request.
    if (opts.signal?.aborted) {
      releaseSlot();
      return abortedSilent("caller signal aborted while queued", "caller");
    }
    // Background fetches re-check the user-activity gate post-slot.
    // A user click that landed while we were queued should silence us
    // here — same outcome as the entry check above, just applied to
    // backgrounds that were already past the entry point.
    if (!isUser && (userFetchesInFlight > 0 || Date.now() < backgroundsBlockedUntil)) {
      releaseSlot();
      return abortedSilent("background fetch preempted by user activity", "yield");
    }
    const localCtrl = new AbortController();
    INFLIGHT_CONTROLLERS.add(localCtrl);
    if (!isUser) BACKGROUND_REGISTRY.add(localCtrl);
    const timer = setTimeout(() => localCtrl.abort(ABORT_REASON_TIMEOUT), timeoutMs);
    // Compose external signal with our timeout signal so caller
    // cancellation propagates while the local timeout still triggers.
    const signal = anySignal([opts.signal, localCtrl.signal]);
    try {
      const headers: Record<string, string> = { ...(opts.headers ?? {}) };
      if (method !== "GET") {
        // Every state-changing request carries the CSRF token; the daemon
        // rejects mutations without it.
        const csrf = getCsrfToken();
        if (csrf) headers["X-CSRF-Token"] = csrf;
      }
      const init: RequestInit = {
        method,
        credentials: "same-origin",
        signal,
        headers,
      };
      if (opts.body !== undefined) {
        if (typeof opts.body === "string" || opts.body instanceof FormData) {
          init.body = opts.body as BodyInit;
        } else {
          init.body = JSON.stringify(opts.body);
          headers["Content-Type"] = "application/json";
        }
      }
      const r = await fetch(url, init);
      noteCacheHeaders(r.headers);
      const contentType = r.headers.get("content-type") ?? "";
      const text = await r.text();
      const looksLikeHtml = /<!DOCTYPE|<html\b/i.test(text.slice(0, 64));

      if (method !== "GET" && !csrfRetried && isCsrfRejection(r.status, text)) {
        // Stale token (the session was refreshed in another tab, or the
        // daemon restarted). Refresh once and try again with the new one.
        csrfRetried = true;
        try { await refreshAuth(); } catch { /* fall through with the 403 */ }
        attempt -= 1;
        continue;
      }

      // 4xx is intentional — bridge / daemon told us no. Don't retry.
      if (r.status >= 400 && r.status < 500) {
        try {
          const data = text ? JSON.parse(text) : null;
          const errObj = data && (data.error ?? null);
          const message =
            (typeof errObj === "string" ? errObj : errObj?.message) ||
            (data && data.message) ||
            `request failed (${r.status})`;
          return { ok: false, error: {
            bridgeUnreachable: false,
            status: r.status,
            message,
            detail: text.slice(0, 500),
            htmlResponse: looksLikeHtml,
          }};
        } catch {
          return { ok: false, error: {
            bridgeUnreachable: false,
            status: r.status,
            message: `request failed (${r.status})`,
            detail: text.slice(0, 500),
            htmlResponse: looksLikeHtml,
          }};
        }
      }

      // 5xx or 0 — retry budget permitting.
      if ((r.status >= 500 || r.status === 0) && attempt < maxTries) {
        lastError = {
          bridgeUnreachable: true,
          status: r.status,
          message: looksLikeHtml
            ? "Mail bridge returned an HTML error page (likely Cloudflare timeout). Retrying..."
            : `bridge returned ${r.status}, retrying...`,
          detail: text.slice(0, 500),
          htmlResponse: looksLikeHtml,
        };
        await sleep(RETRY_DELAYS_MS[attempt - 1] ?? 5_000);
        continue;
      }

      // Parse JSON; if it's HTML, surface a friendly error.
      if (!contentType.includes("json") || looksLikeHtml) {
        return { ok: false, error: {
          bridgeUnreachable: true,
          status: r.status,
          message: looksLikeHtml
            ? "Mail bridge temporarily unavailable. Try again in a few seconds."
            : `non-JSON response (${r.status})`,
          detail: text.slice(0, 500),
          htmlResponse: looksLikeHtml,
        }};
      }
      try {
        const data = JSON.parse(text) as T;
        return { ok: true, status: r.status, data };
      } catch (err) {
        return { ok: false, error: {
          bridgeUnreachable: true,
          status: r.status,
          message: "Mail bridge returned malformed data. Retry in a few seconds.",
          detail: (err instanceof Error ? err.message : String(err)) + " | body=" + text.slice(0, 200),
          htmlResponse: looksLikeHtml,
        }};
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const aborted = (err instanceof DOMException && err.name === "AbortError") || signal.aborted;
      if (aborted) {
        // Caller-initiated abort: rapid nav, unmount, component teardown.
        // Silent return so the new in-flight fetch wins without a flash.
        if (opts.signal?.aborted) {
          const reason = opts.signal.reason;
          return abortedSilent(msg, reason === ABORT_REASON_ABANDONED ? "abandoned" : "caller");
        }
        // Bulk-abort via abortAllBridgeFetches (logout). Same treatment;
        // the SPA is about to unload anyway.
        if (localCtrl.signal.reason === ABORT_REASON_BULK) {
          return abortedSilent(msg, "bulk");
        }
        // User-priority preemption: a user fetch entered and aborted
        // every background controller. Silent drop; the badges will
        // re-fetch on the next tick after the user's grace expires.
        if (localCtrl.signal.reason === ABORT_REASON_USER_PREEMPT) {
          return abortedSilent(msg, "yield");
        }
        // Otherwise the local timeout fired. Do NOT queue another COM
        // request: the timed-out upstream work can continue inside
        // Outlook even after the browser aborts, so retries compound
        // the wedge. Instead, classify liveness with the non-COM
        // health endpoint and return one actionable error.
        const httpAlive = !url.includes("/api/bridge/healthz")
          && await probeBridgeHttpLiveness();
        const specific = opts.timeoutMessage ?? defaultTimeoutMessage(url);
        return { ok: false, error: {
          bridgeUnreachable: true,
          status: 0,
          message: httpAlive
            ? (specific ?? TIMEOUT_MESSAGE_BUSY)
            : TIMEOUT_MESSAGE_DOWN,
          detail: msg,
          htmlResponse: false,
          comBlocked: httpAlive,
          timedOut: true,
        }};
      }
      lastError = {
        bridgeUnreachable: true,
        status: 0,
        message: `network error: ${msg.slice(0, 200)}`,
        detail: msg,
        htmlResponse: false,
      };
      if (attempt < maxTries) {
        await sleep(RETRY_DELAYS_MS[attempt - 1] ?? 5_000);
        continue;
      }
    } finally {
      clearTimeout(timer);
      INFLIGHT_CONTROLLERS.delete(localCtrl);
      BACKGROUND_REGISTRY.delete(localCtrl);
      releaseSlot();
    }
  }
  return { ok: false, error: lastError };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Test hook: drop every module-level cache and scheduling state. */
export function __resetBridgeFetchForTests(): void {
  CACHE.clear();
  INFLIGHT.clear();
  REVALIDATING.clear();
  INFLIGHT_CONTROLLERS.clear();
  BACKGROUND_REGISTRY.clear();
  fetchQueue.length = 0;
  activeFetches = 0;
  userFetchesInFlight = 0;
  backgroundsBlockedUntil = 0;
  cacheEpoch = 0;
}
